// Math.round(value * 100) / 100 alone mis-rounds exact-.5 cases (e.g. 1.005, stored internally as
// 1.00499999999999989..., or 35.855, stored as 35.854999999999996...) down instead of up, because
// of floating-point representation error. A fixed additive epsilon doesn't fix this at every
// magnitude (it's too small relative to larger numbers' own floating-point error), so the
// correction is applied as a tiny relative nudge instead, which scales with the value's own
// magnitude and reliably rounds a trailing 5 up (away from zero) at any size.
const round2 = (value) => {
  const num = Number(value) || 0;
  return Math.round(num * 100 * (1 + Number.EPSILON * 10)) / 100;
};

// estimatedProfitPercent/limitationOfCeilingPercentage/limitationOfFundingPercentage round their
// underlying fraction to 4 decimals (not 2) per the API doc, since they're displayed at 2 decimal
// places of *percentage* precision (×100) — 2 decimals on the raw fraction would collapse to the
// nearest whole percent.
const round4 = (value) => {
  const num = Number(value) || 0;
  return Math.round(num * 10000 * (1 + Number.EPSILON * 10)) / 10000;
};

export const getWeekColumns = (employeeDetails = []) => {
  const weekByStart = {};
  employeeDetails.forEach((employee) => {
    (employee.weeklyBuckets || []).forEach((bucket) => {
      if (!weekByStart[bucket.weekStartDate]) {
        weekByStart[bucket.weekStartDate] = {
          weekStartDate: bucket.weekStartDate,
          weekEndDate: bucket.weekEndDate,
          type: bucket.type,
        };
      }
    });
  });
  return Object.values(weekByStart).sort((a, b) =>
    a.weekStartDate < b.weekStartDate ? -1 : 1,
  );
};

export const getBucketForWeek = (employee, weekStartDate) =>
  (employee.weeklyBuckets || []).find(
    (bucket) => bucket.weekStartDate === weekStartDate,
  );

// Total Labor Dollars = SUMPRODUCT(loaded cost rate, hours) across employees for the week,
// mirroring the CLIN sheet formula `=SUMPRODUCT($D$11:$D$18,G11:G18)`. costRateLoaded is rounded
// to 2 decimals before multiplying by hours, not after, so the persisted rate's own precision
// (not a longer floating-point tail) is what actually gets multiplied.
export const recalculateBucketBurdenCost = (bucket) => {
  const hours = Number(bucket.hours) || 0;
  const costRateLoaded = round2(Number(bucket.costRateLoaded) || 0);
  return round2(hours * costRateLoaded);
};

// ITD = actuals to date, Remaining Forecast = forecast weeks, Total = both combined. Each
// bucket's value is rounded to 2 decimals before adding, not just the final sum.
export const sumEmployeeBuckets = (employee, field, predicate) =>
  round2(
    (employee.weeklyBuckets || [])
      .filter(predicate)
      .reduce((sum, bucket) => sum + round2(Number(bucket[field]) || 0), 0),
  );

export const getEmployeeItdTotal = (employee, field) =>
  sumEmployeeBuckets(employee, field, (bucket) => bucket.type === "Actual");

export const getEmployeeRemainingForecastTotal = (employee, field) =>
  sumEmployeeBuckets(employee, field, (bucket) => bucket.type === "Forecast");

export const getEmployeeGrandTotal = (employee, field) =>
  sumEmployeeBuckets(employee, field, () => true);

// Weekly EAC Summary ITD / Remaining Forecast / Total, per row "mode":
// - "sum" rows (per-week additive amounts, e.g. Total Labor Dollars) are summed over the
//   matching weeks.
// - "lastValue" rows (running/cumulative figures, e.g. Cumulative Cost and Reserve) take the
//   value as of the boundary week instead of summing snapshots; Remaining Forecast is the delta
//   Total introduces over ITD.
export const computeSummaryTotals = (computedWeeklyTotals, rows) => {
  const actualWeeks = computedWeeklyTotals.filter((week) => week.type === "Actual");
  const lastActualWeek = actualWeeks[actualWeeks.length - 1];
  const lastWeek = computedWeeklyTotals[computedWeeklyTotals.length - 1];

  const itd = {};
  const remainingForecast = {};
  const total = {};

  rows.forEach((row) => {
    if (row.mode === "lastValue") {
      const itdValue = lastActualWeek ? Number(lastActualWeek[row.key]) || 0 : 0;
      const totalValue = lastWeek ? Number(lastWeek[row.key]) || 0 : 0;
      itd[row.key] = lastActualWeek ? lastActualWeek[row.key] : null;
      total[row.key] = lastWeek ? lastWeek[row.key] : null;
      remainingForecast[row.key] = round2(totalValue - itdValue);
    } else {
      itd[row.key] = round2(
        actualWeeks.reduce((sum, week) => sum + (Number(week[row.key]) || 0), 0),
      );
      remainingForecast[row.key] = round2(
        computedWeeklyTotals
          .filter((week) => week.type === "Forecast")
          .reduce((sum, week) => sum + (Number(week[row.key]) || 0), 0),
      );
      total[row.key] = round2(
        computedWeeklyTotals.reduce((sum, week) => sum + (Number(week[row.key]) || 0), 0),
      );
    }
  });

  return { itd, remainingForecast, total };
};

export const formatWeekEndDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${month}/${day}/${year}`;
};

export { round2, round4 };
